import Layout from 'components/UI/Layout';
import RichText from 'components/UI/Richtext';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';

import type { Entry } from 'contentful';

import React, { MutableRefObject, ReactElement, useRef, useEffect, useState } from 'react';
import { BrandedSiteProps, PageDates } from 'lib/routing';
import {
  IFragmentInsurance,
  IFragmentService,
  IMetaLocationsSharedDataFields,
  IPageFields,
  IPageLocationFields,
} from 'types/contentful';
import { ProductVariantFieldsWithBrand } from 'components';
import LocationData from 'components/StructuredData/LocationData';
import MedicalBusinessSchema from 'components/StructuredData/MedicalBusinessSchema';
import SharedLocationDataContext from 'context/SharedLocationDataContext';
import { Review, ReviewAverage } from 'lib/reviewinc';
import { Location } from 'components/Contentful/Map';
import HoursOfOperation from 'components/UI/HoursOfOperation';
import NewMap from 'components/UI/NewMap';
import LocationDetailsHeader from 'components/UI/LocationDetailsHeader';
import ProductCarousel from 'components/Contentful/ProductCarousel';
import { Document, BLOCKS } from '@contentful/rich-text-types';
import NearbyClinicCard from 'components/UI/NearbyClinicCard';
import NewInsuranceProviders from 'components/UI/NewInsuranceProviders';
import DoctorCarousel from 'components/Contentful/DoctorCarousel';
import LocationOffering from 'components/UI/LocationOffering';
import SlimHero from 'components/Contentful/Hero/SlimHero';
import ArticleCarousel from 'components/Contentful/ArticleCarousel';
import NumberedListItems from 'components/Contentful/NumberedListItems';
import { BreadcrumbItem } from 'lib/cms/breadcrumbs';
import BreadcrumbNav from 'components/UI/BreadcrumbNav';
import { asFragmentCta } from 'lib/util';

export type LocationDetailsTemplate2025Props = {
  preview: boolean;
  content: IPageLocationFields;
  template: '2025';
  nearbyLocations: Entry<IPageLocationFields>[];
  sharedLocationData: IMetaLocationsSharedDataFields;
  siteData: BrandedSiteProps;
  currentSlug: string;
  customHeader?: ContentfulDocument | null;
  title: string;
  locationSysId: string;
  pageDates?: PageDates;
  reviewAvg?: ReviewAverage;
  reviews?: Review[];
  productWithBrand?: ProductVariantFieldsWithBrand[];
  articles?: IPageFields[];
  breadcrumbs: BreadcrumbItem[];
};

export default function LocationDetailsTemplate2025({
  preview,
  title,
  siteData,
  currentSlug,
  content,
  nearbyLocations,
  sharedLocationData,
  locationSysId,
  customHeader,
  pageDates,
  productWithBrand,
  breadcrumbs,
}: LocationDetailsTemplate2025Props): ReactElement {
  const {
    genericServices,
    insuranceProviders,
    insuranceDisclaimer,
    insuranceSlug,
    meetDoctorsSlug,
    orderContactsSlug,
    articles,
    interpreterAvailabilityText,
    banners,
    scheduleApptCtaText,
  } = sharedLocationData;

  const [activeLocation, setActiveLocation] = useState<Location | null>(null);
  const [hoursHeight, setHoursHeight] = useState<number>(0);
  const hoursContainer = useRef() as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    setActiveLocation(content);
  }, [content]);

  // Function to calculate and set the height of Hours of Operation section
  const calculateHoursHeight = (): void => {
    if (hoursContainer.current) {
      const height = hoursContainer.current.offsetHeight;
      setHoursHeight(height);
    }
  };

  // Effect to calculate height when component mounts and when content changes
  useEffect(() => {
    setTimeout(() => {
      calculateHoursHeight();
    }, 250);

    // Recalculate on window resize
    const handleResize = (): void => {
      calculateHoursHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [content.hours, content.hoursFootNotes, content.hoursInfoText, content.hoursCta]);

  // unique set of services from doctors, locationServices, and genericServices
  const doctorServices = (content.doctors || [])
    .flatMap((doctor) => doctor?.fields?.specialties)
    .filter((item) => item !== undefined);

  const excludeGeneric =
    content.excludeSharedGenericServices !== undefined && content.excludeSharedGenericServices;

  const specialists = doctorServices
    .map((service) => JSON.stringify(service)) // Convert to strings for uniqueness
    .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
    .map((service) => JSON.parse(service)) // Convert back to objects
    .sort((a: IFragmentService, b: IFragmentService) =>
      a.fields.service.localeCompare(b.fields.service)
    );

  let generics: IFragmentService[] = [];

  if (!excludeGeneric) {
    const combinedServices = [...(content.locationServices || []), ...(genericServices || [])];

    generics = Array.from(
      new Set(
        combinedServices
          .filter(
            (service) =>
              !specialists.some(
                (speciality) => speciality.fields.service === service.fields.service
              )
          )
          .map((service) => JSON.stringify(service))
      )
    )
      .map((service) => JSON.parse(service))
      .sort((a: IFragmentService, b: IFragmentService) =>
        a.fields.service.localeCompare(b.fields.service)
      );
  }

  const locationBanners = (banners || [])
    .filter(({ fields }) => fields.locations?.includes(locationSysId))
    .map((banner) => banner.fields.bannerText);

  const defaultCenter = { lat: content.map.lat, lng: content.map.lon };
  const mapContainer = useRef() as MutableRefObject<HTMLDivElement>;

  const productCarouselDescription: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: [
      {
        nodeType: BLOCKS.PARAGRAPH,
        content: [
          {
            data: {},
            marks: [],
            value: 'See yourself in a whole new light-right from home. With our ',
            nodeType: 'text',
          },
          {
            data: {},
            marks: [
              {
                type: 'bold',
              },
            ],
            value: 'virtual try-on ',
            nodeType: 'text',
          },
          {
            data: {},
            marks: [],
            value: 'tool you can find frames that are perfect for you. ',
            nodeType: 'text',
          },
        ],
        data: {},
      },
    ],
  };

  const nextSeoProps = {
    title: content.metaTitle ?? title,
    description:
      content.metaDescription ??
      `Looking for a local eye care doctor in ${content.city}? ${siteData.siteName} has an experienced team at our ${content.name} office. Schedule an exam today!`,
  };

  const combinedInsurers = mergeAndSortInsurers(
    content.specificInsuranceProviders,
    insuranceProviders,
    content.hiddenInsuranceProviders ? content.hiddenInsuranceProviders : []
  );

  const locationDetailsHeader = (): ReactElement => (
    <div className="px-site-component ecp-my">
      <LocationDetailsHeader
        location={content}
        customHeader={customHeader as ContentfulDocument}
        factua={siteData?.factua || false}
      />
    </div>
  );

  const mapHeight = hoursHeight > 0 ? Math.max(hoursHeight - 32, 320) : 320;

  const officeHoursAndLocationMap = (): ReactElement => (
    <div className="ecp-my px-site-component ">
      <div className="pb-6 md:pb-20">
        <div className="w-full h-auto flex flex-col bg-muted lg:flex-row">
          <div className="hidden lg:block w-full lg:w-1/2 bg-muted" ref={hoursContainer}>
            <HoursOfOperation
              locationHours={content.hours}
              insuranceSlug={insuranceSlug}
              footNotes={content.hoursFootNotes}
              daysForFootNotes={content.daysForFootNotes}
              hoursInfoText={content.hoursInfoText}
              hoursNotes={content.hoursNotes}
              interpreterAvailabilityText={interpreterAvailabilityText}
              hoursCta={content.hoursCta?.fields}
            />
          </div>
          <div className="w-full bg-muted lg:w-1/2 p-4">
            {activeLocation ? (
              <div
                className="mx-2 lg:mx-0 px-9 pb-8 lg:pb-0 relative flex flex-col"
                ref={mapContainer}
                style={{
                  minHeight: `${mapHeight}px`,
                  height: `${mapHeight}px`,
                }}
              >
                <div className="flex-1 min-h-0">
                  <NewMap
                    defaultCenter={defaultCenter}
                    mapOptions={{
                      fullscreenControl: false,
                      zoomControl: true,
                      unBlockOnClick: true,
                    }}
                    zoom={10}
                    showPopUp={false}
                    activeLocation={activeLocation}
                    setActiveLocation={setActiveLocation}
                    renderInfoCard={'MapCard'}
                    userLocation={{
                      ...content,
                      map: { lat: content.map.lat, lng: content.map.lon },
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="w-full lg:hidden text-center">
        <HoursOfOperation
          locationHours={content.hours}
          insuranceSlug={insuranceSlug}
          footNotes={content.hoursFootNotes}
          daysForFootNotes={content.daysForFootNotes}
          hoursInfoText={content.hoursInfoText}
          hoursNotes={content.hoursNotes}
          interpreterAvailabilityText={interpreterAvailabilityText}
          hoursCta={content.hoursCta?.fields}
        />
      </div>
    </div>
  );

  const doctors = (): ReactElement | null => {
    if (!content.doctors?.length) return null;
    return (
      <DoctorCarousel
        title=""
        titleElement={
          <div className="flex items-center text-left mb-8">
            <h5 className="bordered-heading bh-location-heading-item text-new-34 lg:text-4xl lg:leading-new-43 font-light text-primary-actual">
              Your {content.name} Team Of Doctors
            </h5>
          </div>
        }
        doctors={content.doctors.map((item) => item.fields)}
      />
    );
  };

  const acceptedInsurance = (): ReactElement | null => {
    if (!combinedInsurers) return null;
    return (
      <NewInsuranceProviders
        heading={'Insurance Accepted At This Location'}
        insurances={combinedInsurers}
        disclaimer={insuranceDisclaimer}
        cta={
          insuranceSlug
            ? {
                title: 'insurance-and-payment',
                text: 'Insurance & Payment',
                slug: insuranceSlug,
                linkToAsset: undefined,
                colorScheme: 'OutlinedTeal',
                openInNewTab: false,
              }
            : undefined
        }
      />
    );
  };

  const servicesOffered = (): ReactElement | null => {
    if (!specialists || specialists.length === 0) return null;
    return (
      <LocationOffering
        id="1"
        services={generics}
        secondRowServices={specialists}
        heading="Eye Care Services"
        secondRowHeading="Specialties At This Location"
      />
    );
  };

  const doctorsSlimHero = (): ReactElement => {
    return (
      <SlimHero
        heading="Looking For A Specific Doctor?"
        image="/images/CTA-2.jpg"
        mobileImage="/images/slim-hero-doctors-mobile.jpg"
        body={{
          nodeType: BLOCKS.DOCUMENT,
          data: {},
          content: [
            {
              nodeType: BLOCKS.PARAGRAPH,
              content: [
                {
                  nodeType: 'text',
                  value: `Browse the doctors' page to find eye care professionals near you and learn more about them.`,
                  marks: [],
                  data: {},
                },
              ],
              data: {},
            },
          ],
        }}
        cta={
          meetDoctorsSlug
            ? asFragmentCta({
                title: 'meet-our-doctors',
                text: 'Meet The Doctors',
                slug: meetDoctorsSlug,
                colorScheme: 'OutlinedTeal',
                openInNewTab: false,
              })
            : undefined
        }
      />
    );
  };

  const productsCarousel = (): ReactElement | null => {
    if (!productWithBrand || productWithBrand.length === 0) return null;
    return (
      <ProductCarousel
        title="See Yourself In A New Pair of Frames"
        description={productCarouselDescription}
        products={productWithBrand}
        cta={{
          title: 'find',
          text: 'Shop All Frames',
          slug: '/shop/eyeglasses',
          linkToAsset: undefined,
          colorScheme: 'OutlinedTeal',
          openInNewTab: false,
        }}
      />
    );
  };

  const orderContactsSlimHero = (): ReactElement => {
    return (
      <SlimHero
        heading="Clear Vision With Total Comfort"
        image="/images/CTA-1.jpg"
        mobileImage="/images/slim-hero-contacts-mobile.jpg"
        body={{
          nodeType: BLOCKS.DOCUMENT,
          data: {},
          content: [
            {
              nodeType: BLOCKS.PARAGRAPH,
              content: [
                {
                  nodeType: 'text',
                  value: 'Find your contacts and get fast, free shipping on every order',
                  marks: [],
                  data: {},
                },
              ],
              data: {},
            },
          ],
        }}
        cta={
          orderContactsSlug
            ? asFragmentCta({
                title: 'order-contacts',
                text: 'Order Contacts',
                slug: orderContactsSlug,
                colorScheme: 'OutlinedTeal',
                openInNewTab: false,
              })
            : undefined
        }
      />
    );
  };

  const articlesCarousel = (): ReactElement | null => {
    if (!articles || articles.length === 0) return null;
    return <ArticleCarousel id="3" heading="Learn More About Your Eye Health" blocks={articles} />;
  };

  const nearbyLocationsComponent = (): ReactElement | null => {
    if (!nearbyLocations || nearbyLocations.length === 0) return null;
    return <NearbyClinicCard locations={nearbyLocations} />;
  };

  const numberedListItemsComponent = (): ReactElement | null => {
    if (
      !sharedLocationData?.howToSchedule ||
      sharedLocationData?.howToSchedule?.fields?.items?.length === 0
    )
      return null;
    return (
      <NumberedListItems
        {...sharedLocationData.howToSchedule.fields}
        siteName={siteData.siteName || ''}
      />
    );
  };

  const freeformText1Component = (): ReactElement | null => {
    if (!content.freeformText1) return null;
    return (
      <div className="ecp-my md:px-0 px-4">
        <RichText document={content.freeformText1} />
      </div>
    );
  };

  const freeformText2Component = (): ReactElement | null => {
    if (!content.freeformText2) return null;
    return (
      <div className="ecp-my md:px-0 px-4">
        <RichText document={content.freeformText2} />
      </div>
    );
  };

  const defaultComponentsOrder = {
    'Location Details Header': locationDetailsHeader(),
    'Office Hours and Location Map': officeHoursAndLocationMap(),
    Doctors: doctors(),
    'Accepted Insurances': acceptedInsurance(),
    'Services Offered': servicesOffered(),
    'Doctors Slim Hero': doctorsSlimHero(),
    'Products Carousel': productsCarousel(),
    'Order Contacts Slim Hero': orderContactsSlimHero(),
    'Articles Carousel': articlesCarousel(),
    'How To Schedule': numberedListItemsComponent(),
    'Freeform Text 1': freeformText1Component(),
    'Freeform Text 2': freeformText2Component(),
    'Nearby Locations': nearbyLocationsComponent(),
  };

  const componentsToDisplay =
    sharedLocationData.locationDetailPagesComponentOrder?.map((component) => {
      return defaultComponentsOrder[component.fields.name as keyof typeof defaultComponentsOrder];
    }) || [];

  let componentNames = Object.keys(defaultComponentsOrder);
  if (componentsToDisplay?.length > 0) {
    componentNames =
      sharedLocationData.locationDetailPagesComponentOrder?.map((component) => {
        return component.fields.name;
      }) || [];
  }

  const getSectionClassName = (index: number, total: number, compName: string): string => {
    if (
      index < total - 1 &&
      ['Doctors Slim Hero', 'Order Contacts Slim Hero', 'Accepted Insurances'].includes(compName)
    ) {
      return 'mb-10 md:mb-20';
    }

    if (index < total - 1 && ['Office Hours and Location Map'].includes(compName)) {
      return 'mb-10 md:mb-0';
    }

    return '';
  };

  return (
    <>
      <Layout
        siteData={siteData}
        preview={preview}
        nextSeoProps={nextSeoProps}
        currentSlug={`locations/${currentSlug}`}
        warningBanners={locationBanners}
        pageDates={pageDates}
      >
        <div className="location-page template-2025">
          {/* Structured Data */}
          <LocationData
            location={content}
            siteName={siteData.siteName}
            domain={siteData.domain}
            description={nextSeoProps.description}
          />
          <MedicalBusinessSchema
            location={content}
            siteName={siteData.siteName}
            domain={siteData.domain}
            paymentsAccepted={sharedLocationData.paymentsAccepted}
            areaServed={[
              `${content.city}, ${content.state.split('-')[0]}`,
              content.state.split('-')[0],
              'United States',
            ]}
            doctors={content.doctors}
            hasProducts={!!(productWithBrand && productWithBrand.length > 0)}
          />

          <SharedLocationDataContext.Provider
            value={{ scheduleAppointmentCtaText: scheduleApptCtaText || '' }}
          >
            {/* Breadcrumbs */}
            <BreadcrumbNav items={breadcrumbs} />

            {/* {numberedListItemsComponent()} */}
            {componentsToDisplay && componentsToDisplay.length > 0
              ? componentsToDisplay.map((component, index) => (
                  <div
                    key={index}
                    className={getSectionClassName(
                      index,
                      componentsToDisplay.length,
                      componentNames[index]
                    )}
                  >
                    {component}
                  </div>
                ))
              : Object.values(defaultComponentsOrder).map((component, index) => (
                  <div
                    key={index}
                    className={getSectionClassName(
                      index,
                      Object.values(defaultComponentsOrder)?.length,
                      componentNames[index]
                    )}
                  >
                    {component}
                  </div>
                ))}
          </SharedLocationDataContext.Provider>
        </div>
      </Layout>
    </>
  );
}

export function mergeAndSortInsurers(
  specificInsuranceProviders: IFragmentInsurance[] = [],
  sharedInsuranceProviders: IFragmentInsurance[] = [],
  hiddenInsurance: IFragmentInsurance[] = []
): IFragmentInsurance[] {
  const mergedMap = new Map<string, IFragmentInsurance>();

  // Add each item to the map using insurer name as key to prevent duplicates
  [...specificInsuranceProviders, ...sharedInsuranceProviders].forEach((item) => {
    const insurerName = item?.fields?.insurer?.trim();
    if (
      insurerName &&
      !mergedMap.has(insurerName) &&
      !hiddenInsurance.some((hidden) => hidden.fields.insurer === insurerName)
    ) {
      mergedMap.set(insurerName, item);
    }
  });

  // Convert map back to array and sort by insurer name
  const mergedSortedArray = Array.from(mergedMap.values()).sort((a, b) => {
    const nameA = a.fields.insurer.toLowerCase();
    const nameB = b.fields.insurer.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return mergedSortedArray;
}
